import { StyleType } from "@iventis/domain-model/model/styleType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { parseRepeatedTimeRangeDataFieldValueToNumber, timeRange, ZERO_TIME_SPAN } from "@iventis/utilities";
import { MapObjectRepeatedTimeRangeValue } from "@iventis/types";
import GeoJSON from "geojson";
import { v4 as uuid } from "uuid";
import {
    getMapObjectNameSystemDatafield,
    getMapObjectLengthSystemDataField,
    getMapObjectRouteTimeSystemDataField,
    getMapObjectAreaSystemDataField,
    getMapObjectCoordinatesSystemDataField,
    getMapObjectQuantityOfModelsOnLineSystemDataField,
    getMapObjectIconRotationSystemDataField,
    getModelAltitudeSystemDataField,
} from "@iventis/datafields";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { createStaticStyleValue, getStaticStyleValue } from "@iventis/layer-style-helpers/src/create-style-value-helpers";
import { CompositionMapObject, MapCursor, TypedFeature } from "../types/internal";
import { LayerStorageScope, MapModuleLayer } from "../types/store-schema";
import { ObjectUpdate } from "../types/events-store";
import { MAP_OBJECT_DEFAULT_NAME, analysisLayerIdPrefix, localSuffix, highlightInfix } from "./constants";
import { MapMode } from "../machines/map-machines.types";
import { defaultLineStyle, defaultPointStyle } from "../utilities/default-style-values";
import { getHighlightCircleRadius, modifyStyleValueFundamentalValues } from "../utilities/style-helpers";

export function tileLayerToLocalLayer(layer: MapModuleLayer): MapModuleLayer {
    if (layer.storageScope === LayerStorageScope.LocalOnly) {
        return layer;
    }

    return {
        ...layer,
        id: getLocalLayerID(layer.id),
        source: getLocalLayerID(layer.id),
    };
}

export function getLocalLayerID(layerId: string) {
    if (layerId.startsWith(analysisLayerIdPrefix)) {
        return layerId;
    }
    return `${layerId}_${localSuffix}`;
}

/**
 * Takes the geometries and rotation property from the first set of objects, and the remaining properties from the second set of objects.
 * Rotation and geometry are preserved.
 * @param targetObjects - The target objects to copy properties to.
 * @param sourceObjects - The source objects to copy properties from.
 * @param projectDatafields - Optional project data fields.
 * */
export function replaceCompositionMapObjectProperties(targetObjects: CompositionMapObject[], sourceObjects: CompositionMapObject[], projectDatafields?: DataField[]) {
    const iconRotationDatafieldId = getMapObjectIconRotationSystemDataField(projectDatafields ?? [])?.id;
    return targetObjects.map(
        (object) =>
            ({
                ...object,
                geojson: {
                    ...object?.geojson,
                    properties: {
                        ...sourceObjects.find((c) => c.objectId === object.objectId)?.geojson.properties,
                        rotation: object?.geojson.properties.rotation,
                        ...(iconRotationDatafieldId ? { [iconRotationDatafieldId]: object?.geojson.properties[iconRotationDatafieldId] } : {}),
                    },
                },
            } as CompositionMapObject)
    );
}

/**
 *  Gets all the system datafields for a map obect (depends on geometry type) and adds them to the properties of the map object.
 *  If value for that datafield is undefined then set to default value.
 */
export function addSystemDataFieldsToMapObject(
    styleType: StyleType,
    properties: { [dataFieldId: string]: unknown } = {},
    projectDataFields: DataField[],
    defaultName: string = MAP_OBJECT_DEFAULT_NAME
) {
    const updatedProperties = {};
    const nameDataField = getMapObjectNameSystemDatafield(projectDataFields);
    if (nameDataField == null) {
        throw new Error("Missing system attribute MapObjectName");
    }

    updatedProperties[nameDataField.id] = properties[nameDataField.id] ?? defaultName;

    if (styleType === StyleType.Line || styleType === StyleType.LineModel) {
        const lengthDataField = getMapObjectLengthSystemDataField(projectDataFields);
        const routeTimeDataField = getMapObjectRouteTimeSystemDataField(projectDataFields);

        if (lengthDataField == null) {
            throw new Error("Missing system attribute MapObjectLength");
        }
        if (routeTimeDataField == null) {
            throw new Error("Missing system attribute RouteTime");
        }

        updatedProperties[lengthDataField.id] = properties[lengthDataField.id] ?? 0;
        updatedProperties[routeTimeDataField.id] = properties[routeTimeDataField.id] ?? ZERO_TIME_SPAN;
    }

    if (styleType === StyleType.Area) {
        const areaDataField = getMapObjectAreaSystemDataField(projectDataFields);

        if (areaDataField == null) {
            throw new Error("Missing system attribute MapObjectArea");
        }

        updatedProperties[areaDataField.id] = properties[areaDataField.id] ?? 0;
    }

    if (styleType === StyleType.Point || styleType === StyleType.Icon || styleType === StyleType.Model) {
        const coordinateDataField = getMapObjectCoordinatesSystemDataField(projectDataFields);

        if (coordinateDataField == null) {
            throw new Error("Missing system attribute Coordinates");
        }

        updatedProperties[coordinateDataField.id] = properties[coordinateDataField.id] ?? "N/A";
    }

    if (styleType === StyleType.Model) {
        const altitudeProjectDataField = getModelAltitudeSystemDataField(projectDataFields);
        if (altitudeProjectDataField == null) {
            throw new Error("Missing system project attribute altitude");
        }
        updatedProperties[altitudeProjectDataField.id] = properties[altitudeProjectDataField.id] ?? 0;
    }

    if (styleType === StyleType.Icon) {
        const iconRotation = getMapObjectIconRotationSystemDataField(projectDataFields);

        if (iconRotation == null) {
            throw new Error("Missing system attribute IconRotation");
        }

        updatedProperties[iconRotation.id] = properties[iconRotation.id] ?? 0;
    }

    if (styleType === StyleType.LineModel) {
        const quantityDataField = getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields);
        if (quantityDataField == null) {
            throw new Error("Missing system attribute Quantity");
        }
        updatedProperties[quantityDataField.id] = properties[quantityDataField.id] ?? 0;
    }

    return updatedProperties;
}

/** Creates a key value pair of attribute Id and it's default value  */
export function getDefaultAttributeValues(layer: Pick<MapModuleLayer, "dataFields">) {
    return layer?.dataFields != null
        ? layer.dataFields.reduce<Record<string, string | boolean | number | Date | MapObjectRepeatedTimeRangeValue>>((attributesWithDefaultValues, attribute) => {
              if (attribute.type === DataFieldType.List && attribute.defaultValue?.listItemId != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: attribute.defaultValue.listItemId };
              }
              if (attribute.type === DataFieldType.Text && attribute.defaultValue?.valueText != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: attribute.defaultValue.valueText };
              }
              if (attribute.type === DataFieldType.Number && attribute.defaultValue?.valueNumber != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: attribute.defaultValue.valueNumber };
              }
              if (attribute.type === DataFieldType.Date && attribute.defaultValue?.valueDate != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: attribute.defaultValue.valueDate };
              }
              if (attribute.type === DataFieldType.Tickbox && attribute.defaultValue?.valueTickbox != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: attribute.defaultValue.valueTickbox };
              }
              if (attribute.type === DataFieldType.Time && attribute.defaultValue?.valueTime != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: attribute.defaultValue.valueTime };
              }
              if (attribute.type === DataFieldType.TimeRange && attribute.defaultValue?.valueTimeRangeFrom != null && attribute.defaultValue?.valueTimeRangeTo != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: timeRange(attribute.defaultValue?.valueTimeRangeFrom, attribute.defaultValue?.valueTimeRangeTo) };
              }
              if (attribute.type === DataFieldType.Hyperlink && attribute.defaultValue?.valueText != null) {
                  return { ...attributesWithDefaultValues, [attribute.id]: attribute.defaultValue.valueText };
              }
              if (attribute.type === DataFieldType.RepeatedTimeRanges && attribute.defaultValue?.valueRepeatedTimeRanges != null) {
                  const repeatedTimeValues = parseRepeatedTimeRangeDataFieldValueToNumber(attribute.defaultValue.valueRepeatedTimeRanges);
                  return { ...attributesWithDefaultValues, [attribute.id]: repeatedTimeValues.map((v) => ({ ...v, id: uuid() })) };
              }
              return attributesWithDefaultValues;
          }, {})
        : {};
}

/** Is the provided array of objects of length 1 and a route object */
export const isSingleRoute = (objects: { geojson?: GeoJSON.Feature<AnySupportedGeometry, MapObjectProperties> }[]) =>
    objects?.length === 1 && objects[0]?.geojson?.properties.modeOfTransport != null;

export const typedFeaturesToObjectUpdates = (features: TypedFeature[]): ObjectUpdate[] =>
    features
        .filter((feature) => feature?.properties?.id != null && feature.properties.layerid != null && feature.geometry != null)
        .map((feature) => ({
            layerId: feature.properties.layerid,
            objectId: feature.properties.id,
            geometry: JSON.parse(JSON.stringify(feature.geometry)),
            properties: JSON.parse(JSON.stringify(feature.properties)),
            waypoints: null,
        }));

/** Maps the mode to it's default cursor */
export const defaultCursor: Record<MapMode, MapCursor> = {
    loading: MapCursor.LOADING,
    read: MapCursor.READ,
    "edit.default": MapCursor.READ,
    "edit.composition": MapCursor.COMPOSITION,
    areaSelect: MapCursor.READ,
    preview: MapCursor.READ,
    "edit.addComment": MapCursor.COMMENT,
    "edit.splitLine": MapCursor.READ,
    "edit.splitLine.asking": MapCursor.READ,
};

export const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** Groups the given list of selected objects by layer id */
export function groupObjectsByLayerId<T extends unknown>(list: T[], getLayerId: (item: T) => string, getObjectId: (item: T) => string): Record<string, string[]> {
    return list.reduce<Record<string, string[]>>((acc, item) => {
        const layerId = getLayerId(item);
        const objectId = getObjectId(item);
        if (acc[layerId] == null) {
            acc[layerId] = [];
        }
        acc[layerId].push(objectId);
        return acc;
    }, {});
}

/*
 * Create a template for a layer. IMPORTANT: Override storage scope
 * @returns The layer
 */
export function createDefaultIventisLayer(): MapModuleLayer {
    return {
        id: null,
        name: null,
        // Storage scope should be overwritten by calling code
        storageScope: undefined,
        visible: null,
        styleType: null,
        areaStyle: null,
        lineStyle: null,
        pointStyle: null,
        iconStyle: null,
        modelStyle: null,
        lineModelStyle: null,
        areaModelStyle: null,
        dataFields: null,
        stamp: "",
        selected: null,
        source: null,
        drawingControls: null,
        locked: false,
    };
}

export function createHighlightLayer(layer: MapModuleLayer): MapModuleLayer {
    switch (layer.styleType) {
        case StyleType.Line:
            return createLineHighlightLayer(layer);
        case StyleType.Area:
            return createLineHighlightLayer(layer);
        case StyleType.Point:
            return createPointHighlightLayer(layer);
        case StyleType.Icon:
            return createPointHighlightLayer(layer);
        case StyleType.LineModel:
            return createLineHighlightLayer(layer);
        case StyleType.AreaModel:
            return createLineHighlightLayer(layer);
        default:
            throw new Error(`No case exists for highlight layers of type ${layer.styleType}`);
    }
}

export function createLineHighlightLayer(layer: MapModuleLayer): MapModuleLayer {
    return {
        ...createDefaultIventisLayer(),
        id: `${layer.id}_${highlightInfix}`,
        storageScope: layer.storageScope,
        source: layer.source,
        stamp: "",
        selected: false,
        name: layer.name,
        visible: layer.visible,
        styleType: StyleType.Line,
        areaStyle: null,
        lineStyle: {
            ...defaultLineStyle,
            styleType: StyleType.Line,
            colour: createStaticStyleValue("yellow"),
            width: createStaticStyleValue(calculateHighlightWidth(layer)),
            opacity: createStaticStyleValue(1),
            offset: createStaticStyleValue(calculateHighlightLayerOffset(layer)),
            blur: createStaticStyleValue(5),
        },
        drawingControls: null,
    };
}

export function calculateHighlightWidth(layer: MapModuleLayer) {
    switch (layer.styleType) {
        case StyleType.Line: {
            const lineWidth = getStaticStyleValue(layer.lineStyle.width);
            const outlineWidth = getStaticStyleValue(layer.lineStyle.outline) ? getStaticStyleValue(layer.lineStyle.outlineWidth) : 0;
            return lineWidth + outlineWidth + 20;
        }
        default:
            return 20;
    }
}

export function calculateHighlightLayerOffset(layer: MapModuleLayer) {
    switch (layer.styleType) {
        case StyleType.Area:
            return getStaticStyleValue(layer.areaStyle.outline) === true ? getStaticStyleValue(layer.areaStyle.outlineWidth) * -1 : 0;
        case StyleType.Line:
            return getStaticStyleValue(layer.lineStyle.offset);
        default:
            return 0;
    }
}

function createPointHighlightLayer(layer: MapModuleLayer): MapModuleLayer {
    return {
        ...createDefaultIventisLayer(),
        id: `${layer.id}_${highlightInfix}`,
        storageScope: layer.storageScope,
        source: layer.source,
        selected: false,
        name: layer.name,
        visible: layer.visible,
        styleType: StyleType.Point,
        pointStyle: {
            ...defaultPointStyle,
            colour: createStaticStyleValue("yellow"),
            outline: createStaticStyleValue(false),
            blur: createStaticStyleValue(0.3),
            radius:
                layer.styleType === StyleType.Point
                    ? modifyStyleValueFundamentalValues<number>(layer.pointStyle.radius, (value) => {
                          const outlineOffset = getStaticStyleValue(layer.pointStyle.outline) ? getStaticStyleValue(layer.pointStyle.outlineWidth) : 0;
                          return getHighlightCircleRadius(value + outlineOffset);
                      })
                    : modifyStyleValueFundamentalValues(layer.iconStyle.size, (value) => {
                          // The base size of the highlight area for icons, gets scaled by the icon's scale
                          const baseIconHighlightSizePx = 30;
                          return baseIconHighlightSizePx * value;
                      }),
        },
        drawingControls: null,
    };
}

/**
 * Check if a object has the correct geometry for showing the delete coordinate button on the map
 * Valid objects are not points, a line with more than a single coordinate and an area with more than 2 coordinates
 */
export function isObjectValidForShowingDeleteCoordinate(object: GeoJSON.Feature<AnySupportedGeometry, MapObjectProperties>) {
    if (object == null) {
        return false;
    }

    switch (object.geometry.type) {
        case "Point":
            return false;
        case "LineString":
            return object.geometry.coordinates.length > 1;
        case "Polygon":
            return object.geometry.coordinates[0].length > 2;
        default:
            return false;
    }
}
